import { Action, createFeatureSelector, createReducer, createSelector, on } from '@ngrx/store';
import { Order } from '@shared/models/order';
import { createEntityAdapter, EntityState } from '@ngrx/entity';
import { LoadingState } from '@shared/types/loading-state';
import { defaultPaginationState, PaginationState } from '@shared/models/pagination-state';
import { loadOrders, loadOrdersFailure, loadOrdersSuccess } from '@features/orders/actions/load-orders.actions';
import { loadOrder, loadOrderFailure, loadOrderSuccess } from '../actions/load-order.actions';
import { suspendOrder, suspendOrderFailure, suspendOrderSuccess } from '../shared/actions/suspend-order.actions';
import { cancelOrder, cancelOrderFailure, cancelOrderSuccess } from '../shared/actions/cancel-order.actions';
import {
  unsuspendOrder,
  unsuspendOrderFailure,
  unsuspendOrderSuccess,
} from '../shared/actions/unsuspend-order.actions';
import {
  resetAuthorizationKey,
  resetAuthorizationKeyFailure,
  resetAuthorizationKeySuccess,
} from '@features/orders/actions/reset-authorization-key.actions';
import { setSelectedOrderId } from '../actions/set-selected-order-id.actions';
import { cancelOrderItem, cancelOrderItemFailure, cancelOrderItemSuccess } from '../actions/cancel-order-item.actions';
import {
  applyOrderItemAudit,
  applyOrderItemAuditFailure,
  applyOrderItemAuditSuccess,
} from '../actions/apply-order-item-audit.actions';
import { setOrderFilters } from '../actions/set-order-filters.actions';
import { OrderFilters } from '@shared/models/order-filters';
import { selectAllOfferings } from '@features/offerings/shared/reducers/offerings.reducer';
import { selectConfig, selectUserType } from '@shared/reducers/session.reducer';
import moment from 'moment';
import { sendPaymentLink, sendPaymentLinkFailure, sendPaymentLinkSuccess } from '../actions/send-payment-link.actions';
import { EsetSubscription } from '@shared/models/eset-subscription';
import {
  loadOrderItemAudit,
  loadOrderItemAuditFailure,
  loadOrderItemAuditSuccess,
} from '../actions/load-order-item-audit.actions';
import { OrderItem } from '@shared/models/order-item';
import { RenewalsMetrics } from '@shared/models/renewals-metrics';
import {
  downloadRenewalsCsv,
  downloadRenewalsCsvFailure,
  downloadRenewalsCsvSuccess,
  updateRenewalsCsvProgress,
} from '@features/renewals/actions/download-renewals-csv.action';

export const featureKey = 'orders';

export const ordersAdapter = createEntityAdapter<Order>({
  selectId: (order: Order) => order.attributes.id,
});

export interface State extends EntityState<Order> {
  loadingText: string | undefined;
  loadingState: LoadingState;
  paginationState: PaginationState;
  selectedOrderId?: number;
  filters?: OrderFilters;
  orderItemLoadingText: string | undefined;
  orderItemLoadingState: LoadingState;
  orderItemAuditPreview?: OrderItem;
  renewalsMetrics?: RenewalsMetrics;
}

export const initialState: State = ordersAdapter.getInitialState({
  loadingText: undefined,
  loadingState: 'not_loading' as LoadingState,
  orderItemLoadingText: undefined,
  orderItemLoadingState: 'not_loading' as LoadingState,
  paginationState: defaultPaginationState,
});

export const ordersReducer = createReducer(
  initialState,
  on(loadOrder, setSelectedOrderId, (state, { orderId }) => ({
    ...state,
    selectedOrderId: orderId,
  })),
  on(
    loadOrders,
    loadOrder,
    suspendOrder,
    unsuspendOrder,
    cancelOrder,
    resetAuthorizationKey,
    cancelOrderItem,
    applyOrderItemAudit,
    sendPaymentLink,
    (state) => ({
      ...state,
      loadingState: 'loading' as LoadingState,
    }),
  ),
  on(loadOrderItemAudit, (state) => ({
    ...state,
    orderItemLoadingState: 'loading' as LoadingState,
    orderItemLoadingText: 'Retrieving audit information...',
  })),
  on(loadOrders, (state, { filters }) => ({
    ...state,
    filters: filters,
  })),
  on(setOrderFilters, (state, { filters }) => ({
    ...state,
    filters: filters,
  })),
  on(suspendOrder, (state) => ({
    ...state,
    loadingText: 'Suspending order...',
  })),
  on(unsuspendOrder, (state) => ({
    ...state,
    loadingText: 'Unsuspending order...',
  })),
  on(cancelOrder, (state) => ({
    ...state,
    loadingText: 'Cancelling order...',
  })),
  on(resetAuthorizationKey, (state) => ({
    ...state,
    loadingText: 'Resetting activation key...',
  })),
  on(applyOrderItemAudit, (state) => ({
    ...state,
    loadingText: 'Auditing item...',
  })),
  on(sendPaymentLink, (state) => ({
    ...state,
    loadingText: 'Resending payment link...',
  })),
  on(loadOrdersSuccess, (state, { orders, paginationState, renewalsMetrics }) => {
    return ordersAdapter.setAll(orders, {
      ...state,
      loadingState: 'loaded' as LoadingState,
      paginationState,
      loadingText: undefined,
      renewalsMetrics,
    });
  }),
  on(loadOrderItemAuditSuccess, (state, { orderItem }) => ({
    ...state,
    orderItemAuditPreview: orderItem,
    orderItemLoadingState: 'loaded' as LoadingState,
    orderItemLoadingText: undefined,
  })),
  on(
    loadOrderSuccess,
    suspendOrderSuccess,
    unsuspendOrderSuccess,
    cancelOrderSuccess,
    resetAuthorizationKeySuccess,
    cancelOrderItemSuccess,
    applyOrderItemAuditSuccess,
    (state, { order }) => {
      if (!!order?.attributes?.id) {
        return ordersAdapter.upsertOne(order, {
          ...state,
          loadingState: 'loaded' as LoadingState,
          loadingText: undefined,
          orderItemAuditPreview: undefined,
        });
      }
      return { ...state, loadingState: 'loaded' as LoadingState, loadingText: undefined };
    },
  ),
  on(sendPaymentLinkSuccess, downloadRenewalsCsvSuccess, (state) => ({
    ...state,
    loadingState: 'loaded' as LoadingState,
    loadingText: undefined,
  })),
  on(
    loadOrdersFailure,
    loadOrderFailure,
    suspendOrderFailure,
    unsuspendOrderFailure,
    cancelOrderFailure,
    resetAuthorizationKeyFailure,
    cancelOrderItemFailure,
    applyOrderItemAuditFailure,
    sendPaymentLinkFailure,
    (state) => ({
      ...state,
      loadingState: 'not_loading' as LoadingState,
      loadingText: undefined,
    }),
  ),
  on(loadOrderItemAuditFailure, downloadRenewalsCsvFailure, (state) => ({
    ...state,
    loadingState: 'not_loading' as LoadingState,
    loadingText: undefined,
  })),
  on(downloadRenewalsCsv, (state: State, { filters }) => ({
    ...state,
    filters: filters,
    loadingState: 'loading' as LoadingState,
    loadingText: 'This might take a while...',
  })),
  on(updateRenewalsCsvProgress, (state, { paginationState }) => ({
    ...state,
    loadingText: `Processed ${paginationState.page * paginationState.per_page} of ${
      paginationState.total_records
    } records...`,
  })),
);

export function reducer(state: State | undefined, action: Action) {
  return ordersReducer(state, action);
}

export const ordersFeatureSelector = createFeatureSelector<State>(featureKey);

const { selectAll, selectEntities } = ordersAdapter.getSelectors(ordersFeatureSelector);

export const selectAllOrders = selectAll;

export const selectOrderById = (orderId: number) =>
  createSelector(selectEntities, (entities) => (orderId == undefined ? undefined : entities[orderId]));

export const selectSelectedOrder = createSelector(
  selectEntities,
  ordersFeatureSelector,
  (entities, state) => entities[state.selectedOrderId || ''],
);

export const orderHistorySortedByDate = createSelector(selectSelectedOrder, (order) => {
  const history = order?.attributes.history_items ? [...order?.attributes.history_items] : [];
  return history?.sort().reverse();
});

export const selectOrdersLoadingState = createSelector(ordersFeatureSelector, (state) => state.loadingState);

export const selectOrdersLoadingText = createSelector(ordersFeatureSelector, (state) => state.loadingText);

export const selectOrderItemLoadingState = createSelector(
  ordersFeatureSelector,
  (state) => state.orderItemLoadingState,
);

export const selectOrderItemLoadingText = createSelector(ordersFeatureSelector, (state) => state.orderItemLoadingText);

export const selectOrdersPaginationState = createSelector(ordersFeatureSelector, (state) => state.paginationState);

export const selectOrderFilters = createSelector(ordersFeatureSelector, (state) => state.filters);

export const selectOrderItemAuditPreview = createSelector(
  ordersFeatureSelector,
  (state) => state.orderItemAuditPreview,
);

export const selectSelectedOrderItemsPricing = createSelector(
  selectSelectedOrder,
  selectAllOfferings,
  (order, offerings) =>
    order?.attributes.order_items.map((order_item) => {
      const offering = offerings.find(
        (offering) => offering.attributes.code === order_item.attributes.offering_code.toString(),
      );
      return {
        order_item,
        offering,
      };
    }),
);

export const selectSelectedOrderHasActiveEsetServices = createSelector(
  selectSelectedOrder,
  selectAllOfferings,
  (order, offerings) =>
    order?.attributes.order_items.some(
      (order_item) =>
        offerings.some((offering) => {
          return (
            offering.attributes.code === order_item.attributes.offering_code.toString() &&
            offering.attributes?.offering_type == 'shared_services'
          );
        }) && (order_item.attributes.itemable as EsetSubscription)?.attributes?.subscription_status === 'active',
    ) || false,
);

export const selectCanCancelSelectedOrder = createSelector(
  selectSelectedOrder,
  selectUserType,
  selectConfig,
  (order, userType, config) => {
    let hasActiveMonthlySubscription = order?.attributes.order_items.some(
      (order_item) =>
        order_item.attributes.period == 'month_1' &&
        order_item.attributes.billing_interval == 'daily' &&
        (order_item.attributes.itemable as EsetSubscription)?.attributes?.subscription_status === 'active',
    );
    let hasActiveAnnualSubscription = order?.attributes.order_items.some(
      (order_item) =>
        ['year_1', 'year_2', 'year_3'].includes(order_item.attributes.period) &&
        (order_item.attributes.itemable as EsetSubscription)?.attributes?.subscription_status === 'active',
    );
    if (
      (userType != 'intranet' &&
        (['online_store', 'global_e_store', 'auto_renewal_system'].includes(order?.attributes.order_source!) ||
          order?.attributes.billing_group_id != order?.attributes.parent_group_id)) ||
      order?.attributes.order_source === 'api' ||
      ['oem', 'retail', 'mass_retail', 'isp', 'msp'].includes(order?.attributes.order_type!) ||
      (hasActiveMonthlySubscription && hasActiveAnnualSubscription)
    ) {
      return false;
    }

    const hasInactiveLicenses = order?.attributes.order_items.some(
      (order_item) =>
        (order_item.attributes.itemable as EsetSubscription)?.attributes?.subscription_status !== 'active',
    );
    const daysAfterOrderDate = moment().diff(moment(order?.attributes.date), 'days');
    if (
      ((userType == 'intranet' && daysAfterOrderDate <= config?.limits.cancel_intranet!) ||
        (userType != 'intranet' && daysAfterOrderDate <= config?.limits.cancel_pzone!)) &&
      !hasInactiveLicenses &&
      order?.attributes.order_status !== 'cancelled'
    ) {
      return true;
    }
    return false;
  },
);

export const selectRenewalsMetrics = createSelector(ordersFeatureSelector, (state) => state.renewalsMetrics);

export const selectSuspendOrUnsuspendSelectedOrder = createSelector(
  selectSelectedOrder,
  selectUserType,
  (order, userType) => {
    const hasInactiveLicenses = order?.attributes.order_items.some(
      (order_item) =>
        (order_item.attributes.itemable as EsetSubscription)?.attributes?.subscription_status !== 'active',
    );
    if (
      userType == 'intranet' &&
      order?.attributes.order_source !== 'api' &&
      !['nfr', 'trial', 'business_trial', 'oem', 'retail', 'mass_retail', 'msp', 'isp'].includes(
        order?.attributes.order_type!,
      ) &&
      (order?.attributes.order_status === 'suspended' ||
        (order?.attributes.order_status !== 'cancelled' && !hasInactiveLicenses))
    ) {
      if (order?.attributes.order_status === 'suspended') {
        return 'unsuspend';
      } else {
        return 'suspend';
      }
    }
    return '';
  },
);
